import { createUseStyles, Styles } from '@pff-consumer/tweed-ui/web-styles'

interface RecurlyModalStyles {
  containerOverlay: Styles
  drawer: Styles
  header: Styles
  closeIcon: Styles
  closeButton: Styles
  planPriceSection: Styles
  planPriceDescriptionContainer: Styles
  planPriceDescriptionLineOne: Styles
  planPrice: Styles
  totalPrice: Styles
  couponCode: Styles
  copyCode: Styles
  mdsSubscribeButton: Styles
  subscribeButton: Styles
  mdsStickerContainer: Styles
  mdsSticker: Styles
  toastMessage: Styles
  toastMessageShow: Styles
  toastMessageText: Styles
}

interface RecurlyModalStylesProps {
  isDesktop: boolean
  width: number
}

export const useStyles = createUseStyles<RecurlyModalStyles, RecurlyModalStylesProps>((theme) => {
  return {
    '@keyframes fadein': {
      from: { opacity: 0, bottom: 0 },
      to: { opacity: 1, bottom: 30 },
    },
    '@keyframes fadeout': {
      from: { opacity: 1, bottom: 30 },
      to: { opacity: 0, bottom: 0 },
    },
    containerOverlay: {
      position: 'fixed',
      top: '0',
      bottom: '0',
      left: '0',
      right: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: 1,
      pointerEvents: 'all',
      transition: 'opacity 200ms ease-in',
      zIndex: 99999,
      backdropFilter: `brightness(0.2)`,
    },
    drawer: ({ width, isDesktop }) => ({
      position: 'relative',
      padding: isDesktop ? theme.calcUnit(theme.spacing.s6) : theme.calcUnit(theme.spacing.s4),
      marginTop: theme.calcUnit(theme.spacing.s4),
      background: theme.colors.textColor,
      width: '100%',
      maxWidth: theme.calcUnit(440),
      minWidth: theme.calcUnit(375),
      height: width <= 440 ? '100%' : 'auto',
      overflow: 'auto',
      ...(width > 440 && { borderRadius: theme.calcUnit(theme.borderRadius.roundedLg) }),
      // Carousel dots style overrides
      '& .carousel .control-dots .dot': {
        boxShadow: 'none',
        background: theme.colors.modalBackground,
      },
      '& .carousel .control-dots .dot.selected': {
        background: theme.colors.webInverseTextColor,
      },
    }),
    closeButton: ({ isDesktop }) => ({
      position: 'absolute',
      right: theme.calcUnit(12),
      top: isDesktop ? theme.calcUnit(16) : theme.calcUnit(12),
      height: theme.calcUnit(32),
      width: theme.calcUnit(32),
      backgroundColor: theme.colors.inverseTextColor,
      borderRadius: theme.calcUnit(theme.borderRadius.roundedFull),
      cursor: 'pointer',
    }),
    closeIcon: {
      position: 'absolute',
      opacity: '0.8',
      top: theme.calcUnit(4),
      right: theme.calcUnit(4),
    },
    header: {
      fontFamily: 'Archivo',
      fontWeight: '600',
      fontSize: theme.calcUnit(24),
      lineHeight: theme.calcUnit(29),
      textTransform: 'uppercase',
      color: theme.colors.webInverseTextColor,
      marginTop: theme.calcUnit(theme.spacing.s1),
    },
    planPriceSection: {
      backgroundColor: theme.colors.inverseTextColor,
      marginTop: theme.calcUnit(theme.spacing.s3),
      border: '1px dashed #B3B3B3',
      borderRadius: theme.calcUnit(theme.borderRadius.roundedDefault),
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
    },
    planPriceDescriptionContainer: {
      padding: theme.calcUnit(theme.spacing.s3),
    },
    planPriceDescriptionLineOne: {
      fontFamily: 'Archivo',
      color: theme.colors.webInverseTextColor,
      fontWeight: '600',
      fontSize: theme.calcUnit(16),
      lineHeight: theme.calcUnit(24),
    },
    planPrice: {
      fontFamily: 'Archivo',
      color: theme.colors.subscriptionPlanPriceColor,
      fontWeight: '500',
      fontSize: theme.calcUnit(12),
      lineHeight: theme.calcUnit(20),
    },
    totalPrice: {
      textDecoration: 'line-through',
    },
    couponCode: {
      fontFamily: 'Archivo',
      color: theme.colors.textColor,
    },
    copyCode: {
      fontFamily: 'Archivo',
      color: '#3388FF',
    },
    mdsSubscribeButton: {
      fontWeight: '600',
      fontSize: theme.calcUnit(16),
      lineHeight: theme.calcUnit(24),
      backgroundColor: theme.colors.backgroundColor,
      color: theme.colors.textColor,
      borderTopRightRadius: theme.calcUnit(theme.borderRadius.roundedDefault),
      borderBottomRightRadius: theme.calcUnit(theme.borderRadius.roundedDefault),
      padding: '10px 12px',
      cursor: 'pointer',
    },
    subscribeButton: {
      fontFamily: 'Archivo',
      fontWeight: '600',
      fontSize: theme.calcUnit(16),
      lineHeight: theme.calcUnit(24),
      backgroundColor: theme.colors.backgroundColor,
      color: theme.colors.textColor,
      borderRadius: theme.calcUnit(theme.borderRadius.roundedDefault),
      padding: '20px 16px',
      width: '100%',
      marginTop: theme.calcUnit(theme.spacing.s3),
      cursor: 'pointer',
    },
    mdsStickerContainer: ({ isDesktop }) => ({
      position: 'absolute',
      right: isDesktop ? theme.calcUnit(theme.spacing.s6) : 0,
      zIndex: 9999,
    }),
    mdsSticker: {
      height: theme.calcUnit(150),
    },
    toastMessage: {
      visibility: 'hidden',
      position: 'fixed',
    },
    toastMessageShow: {
      visibility: 'visible',
      animation: '$fadein 0.5s, $fadeout 0.5s 2.5s',
      backgroundColor: theme.colors.webInverseTextColor,
      color: theme.colors.textColor,
      textAlign: 'center',
      position: 'fixed',
      zIndex: 99999999,
      left: '50%',
      bottom: theme.calcUnit(30),
      fontSize: theme.calcUnit(14),
      fontWeight: '600',
      lineHeight: theme.calcUnit(20),
      minWidth: theme.calcUnit(250),
      marginLeft: theme.calcUnit(-125),
      borderRadius: theme.calcUnit(theme.borderRadius.roundedSm),
      flexDirection: 'row',
      display: 'flex',
      gap: 10,
    },
    toastMessageText: {
      display: 'flex',
      alignItems: 'center',
    },
  }
})
